import type { BufferGeometry, Texture } from "three";
import { runWhenGlobalIdle, type IdleDeadline } from "../../../base/common/async.js";
import { Emitter, type Event } from "../../../base/common/events.js";
import { Disposable, type IDisposable } from "../../../base/common/lifecycle.js";
import { createDecorator } from "../../instantiation/common/registry.js";
import { ILogService } from "../../log/common/log.js";
import { IResourceService, type IResourceEntry, type ResourceKind } from "../../resources/common/resources.js";
import { estimateGeometryMemory } from "./helper.geometries.js";
import { analyzeGeometryVram } from "../common/geometrySavings.js";
import { estimateTextureMemory, estimateTextureCompressionSaving, estimateHdrEnvironmentSaving, isHdrTexture, isGeneratedTexture } from "./helper.textures.js";
import { isCompressedTexture } from "./helper.js";
import { resourceAllocKeys, resourceContentKey } from "./helper.memory.js";
import { objectPathLabel } from "../common/objectPathLabel.js";

// #region model

/** A set of byte-identical resources (same content fingerprint) of one kind. */
export interface ResourceGroupInfo {
    readonly contentKey: string;
    readonly kind: ResourceKind;
    /** ids of the member entries (primitive — never live objects) */
    readonly entryIds: number[];
    readonly count: number;
    /** true = one allocation reused N× (no waste); false = ≥2 separate copies */
    readonly shared: boolean;
    /** real memory of the group (allocations counted once) */
    readonly cpu: number;
    readonly vram: number;
    /** reclaimable by collapsing to one allocation (per tier) */
    readonly reclaimCpu: number;
    readonly reclaimVram: number;
}

export interface ResourceKindReport {
    readonly kind: ResourceKind;
    /** real memory of the kind (shared once, duplicates counted N×) */
    readonly cpu: number;
    readonly vram: number;
    readonly redundantCpu: number;
    readonly redundantVram: number;
    /** identical-content groups with ≥2 members, sorted by reclaimable desc */
    readonly groups: ResourceGroupInfo[];
}

/** Per-entry facts the analyzer already computed while walking the resources. */
export interface EntryMemory {
    readonly cpu: number;
    readonly vram: number;
    /** geometry vertex count (0/undefined for textures) */
    readonly verts?: number;
    /** texture pixel dimensions */
    readonly width?: number;
    readonly height?: number;
}

export interface EntryDuplication {
    /** distinct allocations of this content (≥2) — the worst of CPU/GPU tiers */
    readonly copies: number;
    readonly shared: boolean;
    readonly reclaimCpu: number;
    readonly reclaimVram: number;
    readonly contentKey: string;
}

/** One uncompressed texture worth compressing — for the savings breakdown. */
export interface CompressibleTextureInfo {
    /** HDR (float) data: the fix is FastHDR (KTX2 UASTC HDR), not basis — and it
     *  cuts the DOWNLOAD as well, which no other texture saving here does */
    readonly hdr?: boolean;
    /** the texture's name, or a dimensions/pointer fallback when it has none */
    readonly name: string;
    /** VRAM it would save by GPU-compressing it, bytes */
    readonly saving: number;
    /** what it costs in VRAM today — the saving alone cannot say whether it matters */
    readonly bytes?: number;
    readonly width?: number;
    readonly height?: number;
    /** the model file it was loaded from, when known (getOrigin) */
    readonly model?: string;
    /** the texture's three uuid — lets the UI link it to a select/inspect action */
    readonly uuid?: string;
    /** a material (+slot) that samples it, when known — a second select target */
    readonly material?: string;
    readonly materialUuid?: string;
}

export interface ResourceAnalysisReport {
    /** how many registry entries this pass actually measured. ZERO with a
     *  non-empty registry means "not measured yet", which reads very differently
     *  from "measured, and it is zero" — a consumer must not print the latter. */
    readonly entriesMeasured: number;
    readonly byKind: Partial<Record<ResourceKind, ResourceKindReport>>;
    readonly totalRedundantCpu: number;
    readonly totalRedundantVram: number;
    /** VRAM reclaimable by GPU-compressing uncompressed textures (estimate; excludes
     *  render targets & already-compressed). Geometry savings could join later. */
    readonly totalCompressibleVram: number;
    /** the heaviest compressible textures (deduped by allocation, heaviest first) —
     *  so the UI/AI can name WHICH textures to compress, not just a total. */
    readonly compressibleTextures: readonly CompressibleTextureInfo[];
    /** VRAM reclaimable by QUANTIZING mesh attributes and narrowing indices.
     *  Kept apart from the texture total because the fix is different — and
     *  because Draco/meshopt shrink the DOWNLOAD without touching this number. */
    readonly totalGeometryVram: number;
    /** the heaviest quantizable geometries, same rules as the texture list. */
    readonly compressibleGeometries: readonly CompressibleGeometryInfo[];
    /** VRAM reclaimable on HDR ENVIRONMENTS by shipping FastHDR (KTX2 UASTC HDR)
     *  instead of EXR/HDR. Apart from the texture total because basis cannot hold
     *  HDR at all, and because this one also cuts the download ~3x. */
    readonly totalHdrVram: number;
}

/** One geometry named in the savings breakdown. Bytes. */
export interface CompressibleGeometryInfo {
    readonly name: string;
    readonly saving: number;
    /** what it costs in VRAM today — the saving alone cannot say whether it matters */
    readonly bytes?: number;
    readonly verts?: number;
    /** the model file it was loaded from, when known */
    readonly model?: string;
    readonly uuid?: string;
    /** attributes still stored as full floats */
    readonly quantizable?: readonly string[];
    /** no index buffer — indexing usually helps a lot, by an amount we do not guess */
    readonly nonIndexed?: boolean;
    /** names of the objects that draw it — a geometry is usually unnamed, so this
     *  is what lets somebody find it in the scene */
    readonly usedBy?: readonly string[];
}

export const IResourceAnalysisService = createDecorator<IResourceAnalysisService>("resourceAnalysisService");

/**
 * Owns the duplicate/shared memory analysis of the tracked resources — the DATA
 * MODEL, not the UI. Consumed by the Resources panel and the AI resources tool.
 * Recomputes incrementally on idle after the (debounced) resource-change event
 * so hashing thousands of buffers never stalls the (possibly host) page; live
 * objects are dereferenced transiently and NEVER held across idle slices.
 */
export interface IResourceAnalysisService {
    readonly _serviceBrand: undefined;
    /** Fires when a recompute pass completes (report/lookups refreshed). */
    readonly onDidChangeAnalysis: Event<void>;
    /** Latest report, or null before the first pass finishes. */
    getReport(): ResourceAnalysisReport | null;
    getEntryMemory(entryId: number): EntryMemory | undefined;
    getEntryDuplication(entryId: number): EntryDuplication | undefined;
}

// #endregion

/** Kinds that carry GPU/CPU memory and a content fingerprint. */
const ANALYZED_KINDS: ResourceKind[] = ["texture", "geometry"];
/** Minimum entries per idle slice (so we make progress even under time pressure). */
const CHUNK_MIN = 16;

/**
 * How much of the analysis runs per idle slice.
 *
 * The pass is deliberately lazy — hashing thousands of buffers must never stall
 * a page we do not own — but on a busy page "lazy" became "never": idle
 * callbacks are rare while it renders hard, and 16 entries per callback left
 * VRAM unknown for a long time after the panel asked for it (no visible cause,
 * which is the worst kind of slow).
 *
 * The FIRST pass is the one someone is waiting on, so it gets a real time
 * budget and an idle DEADLINE (requestIdleCallback fires within it whether the
 * page settles or not). Later refreshes keep the polite behaviour.
 */
/** state of the analysis loop — see shouldStartAnalysisPass */
export interface AnalysisPassState { running: boolean; pending: boolean }

/**
 * May a new pass start now?
 *
 * Restarting an in-flight pass looks harmless and is not: the resource tracker
 * re-registers every node on its 2 s sweep, so on a page where a full analysis
 * needs more idle time than that, every pass was cancelled before it could
 * publish and VRAM stayed unknown forever. A request that arrives mid-pass is
 * REMEMBERED instead, so the running one always finishes.
 */
export function shouldStartAnalysisPass(state: AnalysisPassState): boolean {
    if (state.running) { state.pending = true; return false; }
    return true;
}

/** Takes the remembered request, if any — exactly once. */
export function takePendingAnalysisPass(state: AnalysisPassState): boolean {
    if (!state.pending) return false;
    state.pending = false;
    return true;
}

export function analysisSlice(
    firstPass: boolean,
    previous?: { hasEntries: boolean },
): { minEntries: number, budgetMs: number, idleTimeoutMs: number } {
    // A report that measured NOTHING is not an answer. The registry fills
    // asynchronously (the tracker walks the hierarchy, the page keeps loading),
    // so the first pass often completes empty — and treating that as "we have a
    // report" dropped every following pass to idle-only, which left VRAM stuck
    // at 0 B on a busy page.
    const waitingOnAnAnswer = firstPass || previous?.hasEntries === false;
    return waitingOnAnAnswer
        ? { minEntries: 256, budgetMs: 8, idleTimeoutMs: 300 }
        : { minEntries: CHUNK_MIN, budgetMs: 0, idleTimeoutMs: 0 };
}

interface EntryRecord {
    entryId: number;
    kind: ResourceKind;
    cpu: number;
    vram: number;
    verts?: number;
    width?: number;
    height?: number;
    contentKey: string | null;
    cpuKey: string;
    vramKey: string;
    /** VRAM this resource could save: GPU-compressing a texture, or quantizing a
     *  geometry's attributes / narrowing its indices. 0 when there is nothing. */
    compressibleSaving: number;
    /** geometry: attributes still stored as full floats */
    quantizable?: string[];
    /** geometry: no index buffer */
    nonIndexed?: boolean;
    /** texture: HDR (float) data — FastHDR, not basis, is its fix */
    hdr?: boolean;
    /** name/uuid snapshot + the model it was loaded from — to NAME + LINK the savings */
    name: string;
    uuid?: string;
    model?: string;
    pointer?: string;
    /** objects that draw this geometry — the handle you actually have on it */
    usedBy?: string[];
    /** a material that samples this texture (name/uuid + the slot it binds to,
     *  "map", …) — a second select target and a fallback name */
    materialName?: string;
    materialUuid?: string;
    slot?: string;
}

/**
 * Floor for a row in a savings BREAKDOWN. A 4-vertex mesh giving back 88 bytes is
 * noise in a list you scan for what to act on. The totals still count every byte
 * — only the named lists are filtered.
 */
export const MIN_LISTED_SAVING_BYTES = 64 * 1024;

/** Pick the heaviest compressible textures for the savings breakdown: textures
 *  with something to save, deduped by allocation (same vramKey once), heaviest
 *  first. Pure so it's unit-tested without the resource service. */
export function selectCompressibleTextures(
    recs: readonly Pick<EntryRecord, "kind" | "compressibleSaving" | "vram" | "vramKey" | "name" | "uuid" | "width" | "height" | "model" | "pointer" | "materialName" | "materialUuid" | "slot" | "hdr">[],
    limit = 6,
): CompressibleTextureInfo[] {
    const seen = new Set<string>();
    const out: CompressibleTextureInfo[] = [];
    for (const r of recs) {
        if (r.kind !== "texture" || r.compressibleSaving < MIN_LISTED_SAVING_BYTES) continue;
        if (seen.has(r.vramKey)) continue; // count each allocation once
        seen.add(r.vramKey);
        // name it: the texture's own name → its dimensions → the glTF pointer → a
        // bare "texture" (the material is carried SEPARATELY as its own link now).
        const name = r.name?.trim()
            || (r.width && r.height ? `${r.width}×${r.height} texture` : (r.pointer?.trim() || "texture"));
        const material = r.materialName?.trim()
            ? (r.slot?.trim() ? `${r.materialName.trim()} (${r.slot.trim()})` : r.materialName.trim())
            : undefined;
        out.push({
            name, saving: r.compressibleSaving, bytes: r.vram, width: r.width, height: r.height, model: r.model,
            uuid: r.uuid, material, materialUuid: r.materialUuid, hdr: r.hdr || undefined,
        });
    }
    out.sort((a, b) => b.saving - a.saving);
    return out.slice(0, limit);
}

/** Pick the heaviest quantizable geometries for the savings breakdown — same
 *  rules as {@link selectCompressibleTextures}: something to save, deduped by
 *  allocation, heaviest first, capped (the list is re-sent to the AI every turn). */
export function selectCompressibleGeometries(
    recs: readonly Pick<EntryRecord, "kind" | "compressibleSaving" | "vram" | "vramKey" | "name" | "uuid" | "verts" | "model" | "pointer" | "nonIndexed" | "quantizable" | "usedBy">[],
    limit = 6,
): CompressibleGeometryInfo[] {
    const seen = new Set<string>();
    const out: CompressibleGeometryInfo[] = [];
    for (const r of recs) {
        if (r.kind !== "geometry" || r.compressibleSaving < MIN_LISTED_SAVING_BYTES) continue;
        if (seen.has(r.vramKey)) continue; // count each allocation once
        seen.add(r.vramKey);
        // name it: its own name → its vertex count → the glTF pointer → "mesh"
        const name = r.name?.trim()
            || (r.verts ? `${r.verts.toLocaleString("en-US")} vertex mesh` : (r.pointer?.trim() || "mesh"));
        out.push({
            name, saving: r.compressibleSaving, bytes: r.vram, verts: r.verts, model: r.model, uuid: r.uuid,
            quantizable: r.quantizable, nonIndexed: r.nonIndexed || undefined, usedBy: r.usedBy?.length ? r.usedBy : undefined,
        });
    }
    out.sort((a, b) => b.saving - a.saving);
    return out.slice(0, limit);
}

export class ResourceAnalysisService extends Disposable implements IResourceAnalysisService {
    declare readonly _serviceBrand: undefined;

    private readonly _onDidChangeAnalysis = this._register(new Emitter<void>());
    readonly onDidChangeAnalysis = this._onDidChangeAnalysis.event;

    private _report: ResourceAnalysisReport | null = null;
    private _entryMem = new Map<number, EntryMemory>();
    private _entryDup = new Map<number, EntryDuplication>();
    /** did the last published report actually measure anything? */
    private _reportHasEntries = false;
    private _idle: IDisposable | undefined;

    constructor(
        @IResourceService private readonly resources: IResourceService,
        @ILogService private readonly logger: ILogService,
    ) {
        super();
        this._register(this.resources.onDidChangeResources(() => this._schedule()));
        this._schedule();
    }

    getReport(): ResourceAnalysisReport | null { return this._report; }
    getEntryMemory(entryId: number): EntryMemory | undefined { return this._entryMem.get(entryId); }
    getEntryDuplication(entryId: number): EntryDuplication | undefined { return this._entryDup.get(entryId); }

    private readonly _pass: AnalysisPassState = { running: false, pending: false };

    /** Start an incremental pass, or remember the request if one is running. */
    private _schedule(): void {
        if (!shouldStartAnalysisPass(this._pass)) return;
        this._pass.running = true;
        this._idle?.dispose();
        // snapshot the entry HANDLES (weak wrappers, not the resources) up front
        const entries: IResourceEntry[] = [];
        for (const kind of ANALYZED_KINDS) for (const e of this.resources.entries(kind)) entries.push(e);
        const records: EntryRecord[] = [];
        let i = 0;
        // the pass nobody has an answer from yet is the one being waited on
        const slice = analysisSlice(this._report === null, { hasEntries: entries.length > 0 && this._reportHasEntries });
        const step = (deadline: IdleDeadline): void => {
            let n = 0;
            const started = Date.now();
            while (i < entries.length && (n < slice.minEntries
                || deadline.timeRemaining() > 2
                || (slice.budgetMs > 0 && Date.now() - started < slice.budgetMs))) {
                const rec = this._extract(entries[i]);
                i++; n++;
                if (rec) records.push(rec);
            }
            if (i < entries.length) {
                this._idle = runWhenGlobalIdle(step, slice.idleTimeoutMs || undefined);
            } else {
                this._idle = undefined;
                this._pass.running = false;
                this._publish(records);
                // a change that arrived mid-pass gets its own pass now
                if (takePendingAnalysisPass(this._pass)) this._schedule();
            }
        };
        this._idle = runWhenGlobalIdle(step, slice.idleTimeoutMs || undefined);
    }

    /** Per-entry primitives — derefs transiently, drops the live object on return. */
    private _extract(entry: IResourceEntry): EntryRecord | null {
        if (!entry.alive) return null;
        const live = entry.deref();
        if (!live) return null;
        try {
            const alloc = resourceAllocKeys(live);
            if (!alloc) return null;
            let cpu = 0;
            let vram = 0;
            let verts: number | undefined;
            let width: number | undefined;
            let height: number | undefined;
            let compressibleSaving = 0;
            let quantizable: string[] | undefined;
            let nonIndexed: boolean | undefined;
            let hdr = false;
            if (entry.kind === "texture") {
                const m = estimateTextureMemory(live as Texture); cpu = m.cpu; vram = m.vram;
                const image = (live as { image?: { width?: number; height?: number } }).image;
                if (typeof image?.width === "number") width = image.width;
                if (typeof image?.height === "number") height = image.height;
                // GPU-compression opportunity — excludes already-compressed, render
                // targets (those can't be block-compressed) and GENERATED textures
                // (a DataTexture has no file to re-encode; see isGeneratedTexture)
                const renderTarget = (live as { isRenderTargetTexture?: boolean }).isRenderTargetTexture === true;
                const generated = isGeneratedTexture(live);
                // An HDR environment is a FLOAT texture: basis/KTX2 as used for colour
                // maps is LDR and cannot hold it, so the generic advice is unactionable
                // AND understates the win by 5x. FastHDR is the answer for these.
                // FastHDR is a FILE format too — a generated float texture (a
                // computed IBL/LUT) has nothing to ship either
                hdr = isHdrTexture(live as { type?: unknown }) && !isCompressedTexture(live) && !renderTarget && !generated;
                compressibleSaving = hdr
                    ? estimateHdrEnvironmentSaving(vram)
                    : estimateTextureCompressionSaving(vram, { compressed: isCompressedTexture(live), renderTarget, generated });
            } else if (entry.kind === "geometry") {
                const m = estimateGeometryMemory(live as BufferGeometry); cpu = m.cpu; vram = m.vram;
                verts = (live as { attributes?: { position?: { count?: number } } }).attributes?.position?.count;
                // quantization + index width — the only mesh savings that are EXACT.
                // (Draco/meshopt shrink the download, not this number.)
                const geo = analyzeGeometryVram(live as never);
                compressibleSaving = geo.total;
                if (geo.attributes.length > 0) quantizable = geo.attributes.map(a => a.name);
                nonIndexed = geo.nonIndexed;
            } else {
                return null;
            }
            // where it came from — the source model + glTF pointer, and a material
            // that samples it (+slot), so an unnamed texture can still be named.
            let model: string | undefined;
            let pointer: string | undefined;
            let materialName: string | undefined;
            let materialUuid: string | undefined;
            let slot: string | undefined;
            let usedBy: string[] | undefined;
            if (entry.kind === "geometry" && compressibleSaving > 0) {
                const origin = this.resources.getOrigin(entry);
                model = origin?.model.name || undefined;
                pointer = origin?.pointer;
                // WHICH objects draw it. A geometry is usually unnamed, so the row
                // read "3 MB from 122,770 vertex mesh" — true, and useless for
                // finding the thing in a scene. The meshes that use it are the
                // handle a person (or an AI) actually has.
                // Path, not bare name: three meshes called "Object_6" in one scene
                // make the name useless as a handle. The usage snapshot carries a
                // WeakRef, so the walk happens HERE (once, at analysis time) rather
                // than in the tracking hot path. Deduped — a mesh recorded twice,
                // or several instances under one parent, printed "klitka, klitka,
                // klitka" and said nothing three times.
                const labels = new Set<string>();
                for (const u of this.resources.getUsages(entry)) {
                    const live = (u as { ref?: WeakRef<object> }).ref?.deref();
                    const label = (live && objectPathLabel(live, 3)) || u.name?.trim() || u.type;
                    if (label) labels.add(label);
                    if (labels.size >= 3) break;
                }
                usedBy = [...labels];
            }
            if (entry.kind === "texture" && compressibleSaving > 0) {
                const origin = this.resources.getOrigin(entry);
                model = origin?.model.name || undefined;
                pointer = origin?.pointer;
                const usage = this.resources.getUsages(entry)[0];
                if (usage) { materialName = usage.name?.trim() || usage.type; materialUuid = usage.uuid; slot = usage.slots?.[0]; }
            }
            return { entryId: entry.id, kind: entry.kind, cpu, vram, verts, width, height, contentKey: resourceContentKey(live), cpuKey: alloc.cpu, vramKey: alloc.vram, compressibleSaving, quantizable, nonIndexed, hdr, name: entry.name, uuid: entry.uuid, model, pointer, materialName, materialUuid, slot, usedBy };
        } catch (e) {
            this.logger?.trace?.("resourceAnalysis: extract failed", e);
            return null;
        }
    }

    /** Aggregate the collected primitives into the report + lookups (cheap, sync). */
    private _publish(records: EntryRecord[]): void {
        this._reportHasEntries = records.length > 0;
        const entryMem = new Map<number, EntryMemory>();
        const entryDup = new Map<number, EntryDuplication>();
        const byKind: Partial<Record<ResourceKind, ResourceKindReport>> = {};
        let totalRedundantCpu = 0;
        let totalRedundantVram = 0;
        let totalCompressibleVram = 0;
        let totalGeometryVram = 0;
        let totalHdrVram = 0;

        const byKindRecords = new Map<ResourceKind, EntryRecord[]>();
        for (const r of records) {
            entryMem.set(r.entryId, { cpu: r.cpu, vram: r.vram, verts: r.verts, width: r.width, height: r.height });
            let arr = byKindRecords.get(r.kind);
            if (!arr) byKindRecords.set(r.kind, (arr = []));
            arr.push(r);
        }

        for (const [kind, recs] of byKindRecords) {
            // kind totals — dedupe by allocation (shared counted once)
            const cpuSeen = new Set<string>();
            const vramSeen = new Set<string>();
            let kindCpu = 0;
            let kindVram = 0;
            for (const r of recs) {
                if (!cpuSeen.has(r.cpuKey)) { cpuSeen.add(r.cpuKey); kindCpu += r.cpu; }
                if (!vramSeen.has(r.vramKey)) {
                    vramSeen.add(r.vramKey); kindVram += r.vram;
                    // dedupe by allocation, same as vram — and kept APART by kind:
                    // the texture number means "compress these", the geometry one
                    // means "quantize these", and they are not the same advice
                    if (r.kind === "geometry") totalGeometryVram += r.compressibleSaving;
                    // HDR environments are kept apart: their fix is FastHDR, not
                    // basis — and it cuts the download too, which no other saving
                    // on this list does
                    else if (r.hdr) totalHdrVram += r.compressibleSaving;
                    else totalCompressibleVram += r.compressibleSaving;
                }
            }

            // identical-content groups (≥2 members)
            const byContent = new Map<string, EntryRecord[]>();
            for (const r of recs) {
                if (r.contentKey == null) continue;
                let a = byContent.get(r.contentKey);
                if (!a) byContent.set(r.contentKey, (a = []));
                a.push(r);
            }
            const groups: ResourceGroupInfo[] = [];
            let redundantCpu = 0;
            let redundantVram = 0;
            for (const [key, members] of byContent) {
                if (members.length < 2) continue;
                const distinctCpu = new Set(members.map((m) => m.cpuKey)).size;
                const distinctVram = new Set(members.map((m) => m.vramKey)).size;
                const perCpu = members[0].cpu;
                const perVram = members[0].vram;
                const rCpu = (distinctCpu - 1) * perCpu;
                const rVram = (distinctVram - 1) * perVram;
                redundantCpu += rCpu;
                redundantVram += rVram;
                const shared = rCpu === 0 && rVram === 0;
                groups.push({
                    contentKey: key, kind, entryIds: members.map((m) => m.entryId), count: members.length,
                    shared, cpu: distinctCpu * perCpu, vram: distinctVram * perVram, reclaimCpu: rCpu, reclaimVram: rVram,
                });
                const copies = Math.max(distinctCpu, distinctVram);
                for (const m of members) entryDup.set(m.entryId, { copies, shared, reclaimCpu: rCpu, reclaimVram: rVram, contentKey: key });
            }
            groups.sort((a, b) => Math.max(b.reclaimVram, b.reclaimCpu) - Math.max(a.reclaimVram, a.reclaimCpu));
            byKind[kind] = { kind, cpu: kindCpu, vram: kindVram, redundantCpu, redundantVram, groups };
            totalRedundantCpu += redundantCpu;
            totalRedundantVram += redundantVram;
        }

        this._entryMem = entryMem;
        this._entryDup = entryDup;
        const compressibleTextures = selectCompressibleTextures(records);
        const compressibleGeometries = selectCompressibleGeometries(records);
        this._report = { entriesMeasured: records.length, byKind, totalRedundantCpu, totalRedundantVram, totalCompressibleVram, compressibleTextures, totalGeometryVram, compressibleGeometries, totalHdrVram };
        this._onDidChangeAnalysis.fire();
    }

    override dispose(): void {
        this._idle?.dispose();
        super.dispose();
    }
}
